/**
 * Google Identity Services (GSI) Integration -- Singleton Pattern
 *
 * GSI only allows ONE initialize() call per page. Calling it multiple times
 * (e.g., OneTap + button) causes "initialize() called multiple times" warning
 * and nonce mismatch where the last init overwrites the first.
 *
 * This module uses a singleton pattern: one shared callback dispatcher,
 * one nonce, one initialize() call. Both OneTap and button share it.
 *
 * Nonce flow: raw nonce -> GSI init -> Google embeds raw nonce in JWT -> backend direct-compares
 */

import { loadScript, GSI_SCRIPT_URL } from './loadScript';
import { generateNonce } from './nonce';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GsiInitConfig) => void;
          renderButton: (
            parent: HTMLElement,
            options: GsiButtonConfig
          ) => void;
          prompt: (momentListener?: (notification: PromptMomentNotification) => void) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

/** GSI initialize() configuration */
interface GsiInitConfig {
  client_id: string;
  callback: (response: CredentialResponse) => void;
  nonce?: string;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  context?: 'signin' | 'signup' | 'use';
  ux_mode?: 'popup' | 'redirect';
  login_uri?: string;
  login_hint?: string;
  hd?: string;
  use_fedcm_for_button?: boolean;
  itp_support?: boolean;
}

/** GSI renderButton() options */
interface GsiButtonConfig {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
}

export interface CredentialResponse {
  /** GSI SDK may return undefined/empty credential on FedCM errors, ITP failures, or session edge cases */
  credential: string | undefined;
  select_by?: string;
  clientId?: string;
}

export interface PromptMomentNotification {
  getMomentType: () => string;
  getDismissedReason: () => string;
  getNotDisplayedReason: () => string;
}

/** User-facing error message when Google returns no credential */
export const GSI_EMPTY_CREDENTIAL_ERROR =
  'Google sign-in was cancelled or failed. Please try again.';

// ─────────────────────────────────────────────────────────────────────────────
// Singleton State -- one GSI instance per page load
// ─────────────────────────────────────────────────────────────────────────────

type GsiCallback = (credential: string, gCsrfToken?: string, nonce?: string) => void;
type GsiErrorCallback = (error: string) => void;

/** Current credential callback -- updated by whoever last called renderGoogleButton or showOneTapPrompt */
let activeCallback: GsiCallback | null = null;
let activeErrorCallback: GsiErrorCallback | null = null;
/** The nonce used in the current GSI session */
let activeNonce: string | null = null;
/** Whether GSI has been initialized this page load */
let isInitialized = false;
/** The client ID used for initialization (to detect changes) */
let initializedClientId: string | null = null;

/**
 * Validate that a credential from GSI is a usable non-empty string.
 */
function isValidCredential(credential: unknown): credential is string {
  return typeof credential === 'string' && credential.length > 0;
}

/**
 * Parse g_csrf_token from document.cookie
 */
export function getGoogleCsrfToken(): string | undefined {
  const match = document.cookie.match(/(?:^|;\s*)g_csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1].trim()) : undefined;
}

/** Ensure GSI script is loaded (idempotent via loadScript singleton) */
async function ensureGsiLoaded(): Promise<void> {
  await loadScript(GSI_SCRIPT_URL);
  if (!window.google?.accounts?.id) {
    throw new Error('Google Identity Services not available after script load');
  }
}

/**
 * Shared GSI callback dispatcher.
 * GSI calls this one function; it forwards to whatever activeCallback is set.
 */
function gsiCallbackDispatcher(response: CredentialResponse): void {
  if (!isValidCredential(response.credential)) {
    // eslint-disable-next-line no-console
    console.debug('[GSI] Callback received empty credential -- skipping API call');
    activeErrorCallback?.(GSI_EMPTY_CREDENTIAL_ERROR);
    return;
  }
  const gCsrfToken = getGoogleCsrfToken();
  if (activeCallback && activeNonce) {
    activeCallback(response.credential, gCsrfToken, activeNonce);
  }
}

/**
 * Initialize GSI exactly once per page load (or when client ID changes).
 * Both renderGoogleButton and showOneTapPrompt call this -- only the first
 * call actually runs initialize(). Subsequent calls just update the callback.
 */
async function ensureGsiInitialized(clientId: string): Promise<void> {
  await ensureGsiLoaded();

  // Generate a single nonce for this session
  if (!activeNonce) {
    activeNonce = generateNonce();
  }

  // Only call initialize() once per client ID
  if (isInitialized && initializedClientId === clientId) {
    return;
  }

  const google = window.google!.accounts.id;
  google.initialize({
    client_id: clientId,
    nonce: activeNonce,
    callback: gsiCallbackDispatcher,
    auto_select: true,
    cancel_on_tap_outside: true,
    use_fedcm_for_button: true,
    itp_support: true,
  });

  isInitialized = true;
  initializedClientId = clientId;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Render Google Sign-In button into a container.
 * Uses the singleton GSI instance -- does NOT call initialize() again.
 */
export async function renderGoogleButton(
  container: HTMLElement,
  clientId: string,
  callback: GsiCallback,
  onError?: GsiErrorCallback
): Promise<void> {
  // Set this as the active callback
  activeCallback = callback;
  activeErrorCallback = onError ?? null;

  await ensureGsiInitialized(clientId);

  const google = window.google!.accounts.id;
  const width = Math.min(container.offsetWidth || 400, 400);
  google.renderButton(container, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    shape: 'rectangular',
    width,
  });
}

/**
 * Show Google One Tap prompt -- the floating account picker.
 * Uses the singleton GSI instance -- does NOT call initialize() again.
 */
export async function showOneTapPrompt(
  clientId: string,
  callback: GsiCallback,
  onError?: GsiErrorCallback
): Promise<void> {
  // Set this as the active callback
  activeCallback = callback;
  activeErrorCallback = onError ?? null;

  await ensureGsiInitialized(clientId);

  const google = window.google!.accounts.id;
  google.prompt((notification: PromptMomentNotification) => {
    const notDisplayed = notification.getNotDisplayedReason();
    const dismissed = notification.getDismissedReason();

    if (dismissed === 'user_cancel' || dismissed === 'tap_outside') return;
    if (notDisplayed === 'opt_out_or_no_session') return;
    if (notDisplayed === 'suppressed_by_user') return;

    if (notDisplayed) {
      // eslint-disable-next-line no-console
      console.debug('[OneTap] Not displayed:', notDisplayed);
    }
  });
}

/**
 * @deprecated Use renderGoogleButton directly.
 */
export async function initGoogleSignIn(clientId: string): Promise<void> {
  await ensureGsiInitialized(clientId);
}
